/**
 * Session Manager for handling multiple user sessions (candidate, hr, course_provider, etc.)
 * PRODUCTION VERSION: Uses sessionStorage (not localStorage) for security
 * Works with HTTP-only cookies set by backend
 * Each role gets its own session storage to prevent conflicts
 * Includes device-based session management to prevent concurrent logins
 */

const SESSION_KEYS = {
  candidate: {
    token: 'dutlaunch_candidate_token',
    role: 'dutlaunch_candidate_role',
    user_id: 'dutlaunch_candidate_user_id',
    email: 'dutlaunch_candidate_email',
    profile: 'dutlaunch_candidate_profile'
  },
  hr: {
    token: 'dutlaunch_hr_token',
    role: 'dutlaunch_hr_role',
    user_id: 'dutlaunch_hr_user_id',
    email: 'dutlaunch_hr_email',
    company: 'dutlaunch_hr_company',
    contact_name: 'dutlaunch_hr_contact_name',
    profile: 'dutlaunch_hr_profile'
  },
  course_provider: {
    token: 'dutlaunch_cp_token',
    role: 'dutlaunch_cp_role',
    user_id: 'dutlaunch_cp_user_id',
    email: 'dutlaunch_cp_email',
    username: 'dutlaunch_cp_username',
    company: 'dutlaunch_cp_company',
    profile: 'dutlaunch_cp_profile'
  },
  saas_provider: {
    token: 'dutlaunch_sp_token',
    role: 'dutlaunch_sp_role',
    user_id: 'dutlaunch_sp_user_id',
    email: 'dutlaunch_sp_email',
    username: 'dutlaunch_sp_username',
    company: 'dutlaunch_sp_company',
    profile: 'dutlaunch_sp_profile'
  },
  super_admin: {
    token: 'dutlaunch_sa_token',
    role: 'dutlaunch_sa_role',
    user_id: 'dutlaunch_sa_user_id',
    email: 'dutlaunch_sa_email',
    username: 'dutlaunch_sa_username',
    profile: 'dutlaunch_sa_profile'
  }
};

// Current active session
const CURRENT_SESSION_KEY = 'dutlaunch_current_session';
const LAST_LOGIN_ROLE_KEY = 'dutlaunch_last_login_role';

class SessionManager {
  /**
   * Set session data for a specific role
   */
  static setSession(role, sessionData) {
    const keys = SESSION_KEYS[role];
    if (!keys) {
      console.error(`❌ [SESSION] Invalid role: ${role}`);
      return;
    }

    console.log('🔐 [SESSION] Setting session for role:', role);
    console.log('🔐 [SESSION] Session data received:', {
      hasToken: !!sessionData.access_token,
      tokenPreview: sessionData.access_token ? `${sessionData.access_token.substring(0, 30)}...` : 'NO TOKEN',
      tokenLength: sessionData.access_token?.length,
      hasProfile: !!sessionData.profile,
      profileId: sessionData.profile?.id
    });

    // CRITICAL: Validate token before storing
    if (sessionData.access_token) {
      const token = sessionData.access_token;
      
      // Check if it's a placeholder/fake token
      if (token === 'candidate-token' || token === 'hr-token' || token === 'fake-token' || token.length < 50) {
        console.error('❌ [SESSION] FAKE/PLACEHOLDER TOKEN DETECTED!');
        console.error('❌ [SESSION] Token:', token);
        console.error('❌ [SESSION] This is not a real JWT token. Login may have failed.');
        console.error('❌ [SESSION] NOT storing this session.');
        alert('⚠️ Login failed: Invalid token received. Please try logging in again.');
        return; // Don't store fake tokens
      }
      
      // Check if it looks like a JWT (should have 3 parts)
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('❌ [SESSION] Token does not appear to be a JWT (expected 3 parts, got', parts.length, ')');
        console.error('❌ [SESSION] Token:', token);
        console.error('❌ [SESSION] NOT storing this session.');
        alert('⚠️ Login failed: Invalid token format. Please try logging in again.');
        return; // Don't store invalid tokens
      }
      
      localStorage.setItem(keys.token, sessionData.access_token);
      console.log('✅ [SESSION] Token stored in localStorage:', keys.token);
      console.log('✅ [SESSION] Token preview:', sessionData.access_token.substring(0, 30) + '...');
    } else {
      console.error('❌ [SESSION] No access_token in sessionData!');
      alert('⚠️ Login failed: No token received from server. Please try again.');
      return; // Don't store session without token
    }
    
    localStorage.setItem(keys.role, role);
    
    if (sessionData.profile) {
      localStorage.setItem(keys.profile, JSON.stringify(sessionData.profile));
      
      if (sessionData.profile.id) {
        localStorage.setItem(keys.user_id, sessionData.profile.id);
      }
      
      if (sessionData.profile.email) {
        localStorage.setItem(keys.email, sessionData.profile.email);
      }
      
      // Role-specific profile data
      if (role === 'hr') {
        if (sessionData.profile.company_name) {
          localStorage.setItem(keys.company, sessionData.profile.company_name);
        }
        if (sessionData.profile.contact_name) {
          localStorage.setItem(keys.contact_name, sessionData.profile.contact_name);
        }
      } else if (role === 'course_provider' || role === 'saas_provider') {
        if (sessionData.profile.username) {
          localStorage.setItem(keys.username, sessionData.profile.username);
        }
        if (sessionData.profile.company_name) {
          localStorage.setItem(keys.company, sessionData.profile.company_name);
        }
      } else if (role === 'super_admin') {
        if (sessionData.profile.username) {
          localStorage.setItem(keys.username, sessionData.profile.username);
        }
      }
    }

    // Set as current active session
    localStorage.setItem(CURRENT_SESSION_KEY, role);
    localStorage.setItem(LAST_LOGIN_ROLE_KEY, role);

    // Dispatch custom event to notify components of session update
    window.dispatchEvent(new CustomEvent('sessionUpdated', { 
      detail: { role, sessionData } 
    }));
  }

  /**
   * Get session data for a specific role
   */
  static getSession(role) {
    const keys = SESSION_KEYS[role];
    if (!keys) {
      return null;
    }

    const token = localStorage.getItem(keys.token);
    if (!token) {
      return null;
    }

    const sessionData = {
      access_token: token,
      role: localStorage.getItem(keys.role),
      user_id: localStorage.getItem(keys.user_id),
      email: localStorage.getItem(keys.email),
    };

    // Get profile data
    const profileStr = localStorage.getItem(keys.profile);
    if (profileStr) {
      try {
        sessionData.profile = JSON.parse(profileStr);
      } catch (e) {
        console.error('Error parsing profile data:', e);
      }
    }

    // Role-specific data
    if (role === 'hr') {
      sessionData.company = localStorage.getItem(keys.company);
      sessionData.contact_name = localStorage.getItem(keys.contact_name);
    } else if (role === 'course_provider' || role === 'saas_provider' || role === 'super_admin') {
      sessionData.username = localStorage.getItem(keys.username);
      if (keys.company) {
        sessionData.company = localStorage.getItem(keys.company);
      }
    }

    return sessionData;
  }

  /**
   * Get current active session
   */
  static getCurrentSession() {
    const currentRole = localStorage.getItem(CURRENT_SESSION_KEY);
    if (!currentRole) {
      return null;
    }
    return this.getSession(currentRole);
  }

  /**
   * Switch to a different role session
   */
  static switchSession(role) {
    const session = this.getSession(role);
    if (session) {
      localStorage.setItem(CURRENT_SESSION_KEY, role);
      return session;
    }
    return null;
  }

  /**
   * Clear session for a specific role
   */
  static clearSession(role) {
    const keys = SESSION_KEYS[role];
    if (!keys) {
      return;
    }

    // Clear all role-specific keys
    Object.values(keys).forEach(key => {
      localStorage.removeItem(key);
    });

    // If this was the current session, clear it
    const currentRole = localStorage.getItem(CURRENT_SESSION_KEY);
    if (currentRole === role) {
      localStorage.removeItem(CURRENT_SESSION_KEY);
    }
  }

  /**
   * Clear cached profile data for a specific role (forces fresh API fetch)
   */
  static clearProfileCache(role) {
    const keys = SESSION_KEYS[role];
    if (keys && keys.profile) {
      localStorage.removeItem(keys.profile);
      console.log(`✅ Cleared profile cache for role: ${role}`);
    }
  }

  /**
   * Clear all sessions
   */
  static clearAllSessions() {
    Object.keys(SESSION_KEYS).forEach(role => {
      this.clearSession(role);
    });
    localStorage.removeItem(CURRENT_SESSION_KEY);
    localStorage.removeItem(LAST_LOGIN_ROLE_KEY);
  }

  /**
   * Get all active sessions
   */
  static getAllSessions() {
    const sessions = {};
    Object.keys(SESSION_KEYS).forEach(role => {
      const session = this.getSession(role);
      if (session) {
        sessions[role] = session;
      }
    });
    return sessions;
  }

  /**
   * Check if user is logged in for a specific role
   */
  static isLoggedIn(role) {
    const session = this.getSession(role);
    return session && session.access_token;
  }

  /**
   * Get last login role
   */
  static getLastLoginRole() {
    return localStorage.getItem(LAST_LOGIN_ROLE_KEY);
  }

  /**
   * Legacy compatibility - get current session using old keys
   * This helps with backward compatibility
   */
  static getLegacySession() {
    const token = localStorage.getItem('dutlaunch_token');
    const role = localStorage.getItem('dutlaunch_role');
    
    if (token && role) {
      return {
        access_token: token,
        role: role,
        user_id: localStorage.getItem('dutlaunch_user_id'),
        email: localStorage.getItem('dutlaunch_email'),
        username: localStorage.getItem('dutlaunch_username'),
        company: localStorage.getItem('dutlaunch_company'),
        contact_name: localStorage.getItem('dutlaunch_contact_name')
      };
    }
    
    return null;
  }

  /**
   * Migrate legacy session to new format
   */
  static migrateLegacySession() {
    const legacy = this.getLegacySession();
    if (legacy && legacy.role) {
      // Create profile object from legacy data
      const profile = {
        id: legacy.user_id,
        email: legacy.email,
        username: legacy.username,
        company_name: legacy.company,
        contact_name: legacy.contact_name
      };

      // Set new session
      this.setSession(legacy.role, {
        access_token: legacy.access_token,
        role: legacy.role,
        profile: profile
      });

      // Clear legacy keys
      localStorage.removeItem('dutlaunch_token');
      localStorage.removeItem('dutlaunch_role');
      localStorage.removeItem('dutlaunch_user_id');
      localStorage.removeItem('dutlaunch_email');
      localStorage.removeItem('dutlaunch_username');
      localStorage.removeItem('dutlaunch_company');
      localStorage.removeItem('dutlaunch_contact_name');
      localStorage.removeItem('dutlaunch_candidate_id');
      localStorage.removeItem('dutlaunch_admin_key');

      console.log('Migrated legacy session to new format');
      return true;
    }
    
    return false;
  }
  /**
   * Update profile data for a specific role session
   */
  static updateSessionProfile(role, profileData) {
    const keys = SESSION_KEYS[role];
    if (!keys) {
      console.error(`Invalid role: ${role}`);
      return false;
    }

    // Get existing session
    const existingSession = this.getSession(role);
    if (!existingSession) {
      console.error(`No session found for role: ${role}`);
      return false;
    }

    // Merge new profile data with existing profile
    const updatedProfile = {
      ...existingSession.profile,
      ...profileData
    };

    // Update profile in localStorage
    localStorage.setItem(keys.profile, JSON.stringify(updatedProfile));
    
    console.log('✅ Updated session profile for role:', role, updatedProfile);

    // Dispatch custom event to notify components of session update
    window.dispatchEvent(new CustomEvent('sessionUpdated', { 
      detail: { role, profileData: updatedProfile } 
    }));

    return true;
  }

  /**
   * Debug function to show all localStorage keys
   */
  static debugLocalStorage() {
    const keys = Object.keys(localStorage);
    const dutlaunchKeys = keys.filter(key => key.startsWith('dutlaunch_'));
    console.log('🔍 All dutlaunch localStorage keys:', dutlaunchKeys);
    dutlaunchKeys.forEach(key => {
      console.log(`  ${key}:`, localStorage.getItem(key));
    });
    return dutlaunchKeys;
  }

  /**
   * Force clear all localStorage (for debugging)
   */
  static debugClearAll() {
    const keys = Object.keys(localStorage);
    const dutlaunchKeys = keys.filter(key => key.startsWith('dutlaunch_'));
    console.log('🧹 Clearing all dutlaunch keys:', dutlaunchKeys);
    dutlaunchKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    console.log('✅ All dutlaunch keys cleared');
  }

  /**
   * Validate token format (basic check)
   */
  static isValidToken(token) {
    if (!token || typeof token !== 'string') {
      return false;
    }
    // JWT tokens have 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }
    // Check if each part is base64-like
    return parts.every(part => part.length > 0 && /^[A-Za-z0-9_-]+$/.test(part));
  }

  /**
   * Validate current session token
   */
  static validateCurrentSession() {
    const session = this.getCurrentSession();
    if (!session || !session.access_token) {
      console.log('❌ No current session found');
      return false;
    }
    
    if (!this.isValidToken(session.access_token)) {
      console.log('❌ Invalid token format');
      this.clearAllSessions();
      return false;
    }
    
    console.log('✅ Session token is valid');
    return true;
  }
}

export default SessionManager;
